import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { Alert, AlertIcon, Box, HStack, Spinner, Switch, Text } from "@chakra-ui/react";
import {
  colorFor,
  deliveredTo,
  highlightHtml,
  renderWithFakeData,
  segmentJinja,
  tagName,
  type Vocab,
} from "../lib/jinjaHighlight";

export default function EmailPreview({
  html,
  loading,
  emptyText = "No HTML to preview.",
  subject,
  subjectEditor,
  vocab,
}: {
  html: string | null;
  loading?: boolean;
  emptyText?: string;
  subject?: string | null;
  subjectEditor?: ReactNode;
  vocab: Vocab;
}) {
  const [fake, setFake] = useState(false);

  const srcDoc = useMemo(() => {
    if (!html) return "";
    return fake ? renderWithFakeData(html, vocab) : highlightHtml(html, vocab);
  }, [html, fake, vocab]);

  return (
    <Box>
      <HStack justify="space-between" mb={2}>
        <Text fontWeight="semibold">Preview</Text>
        <HStack>
          <Text fontSize="sm" color="gray.600">
            Fill with sample data
          </Text>
          <Switch
            isChecked={fake}
            isDisabled={!vocab}
            onChange={(e) => setFake(e.target.checked)}
          />
        </HStack>
      </HStack>

      {(subjectEditor !== undefined || subject != null) && (
        <Box mb={3}>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Subject
          </Text>
          {subjectEditor !== undefined ? (
            subjectEditor
          ) : fake ? (
            <Text fontWeight="medium">{renderWithFakeData(subject ?? "", vocab)}</Text>
          ) : (
            <Text fontWeight="medium">
              {segmentJinja(subject ?? "", vocab).map((seg, i) => {
                if (seg.kind === "text") {
                  return (
                    <Text as="span" key={i}>
                      {seg.value}
                    </Text>
                  );
                }
                let title: string | undefined;
                if (seg.kind === "wrong-template") {
                  const name = tagName(seg.value);
                  title = name
                    ? `Only sent to: ${deliveredTo(name, vocab).join(", ")}`
                    : undefined;
                } else if (seg.kind === "unknown") {
                  title = "Not a known variable";
                }
                return (
                  <Text
                    as="span"
                    key={i}
                    color={colorFor(seg.kind)}
                    fontWeight={
                      seg.kind === "wrong-template" || seg.kind === "unknown"
                        ? "600"
                        : undefined
                    }
                    title={title}
                  >
                    {seg.value}
                  </Text>
                );
              })}
            </Text>
          )}
        </Box>
      )}

      {!vocab && html && (
        <Alert status="warning" fontSize="sm" mb={2}>
          <AlertIcon />
          Variable list unavailable — tags aren&apos;t being checked.
        </Alert>
      )}

      {loading ? (
        <Box h="480px" display="flex" alignItems="center" justifyContent="center">
          <Spinner />
        </Box>
      ) : !html ? (
        <Text color="gray.500">{emptyText}</Text>
      ) : (
        <>
          <iframe
            title="Email preview"
            sandbox=""
            srcDoc={srcDoc}
            style={{
              width: "100%",
              height: "480px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              background: "white",
            }}
          />
          {!fake && (
            <HStack mt={2} spacing={4}>
              <HStack spacing={1}>
                <Box w={3} h={3} bg="#2b6cb0" />
                <Text fontSize="xs">recognized tag</Text>
              </HStack>
              <HStack spacing={1}>
                <Box w={3} h={3} bg="#e53e3e" />
                <Text fontSize="xs">unknown / malformed</Text>
              </HStack>
            </HStack>
          )}
        </>
      )}
    </Box>
  );
}
